import React, { createContext, useContext, useState, useEffect } from 'react';
import apiService from '../services/api';
import TwoFactorModal from '../components/modals/TwoFactorModal';
import { getToken, setToken, clearToken } from '../utils/auth';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // 🔐 Estados do 2FA
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFactorData, setTwoFactorData] = useState(null);

  // Wrappers locais que delegam ao helper central (utils/auth.js).
  // Mantidos como variáveis para evitar refatorar todo o componente — a
  // lógica anti-duplicação vive agora no helper.
  const saveToken = (token) => setToken(token);
  const clearAuthData = () => {
    clearToken();
    try { sessionStorage.clear(); } catch { /* noop */ }
  };

  // Verificação de autenticação
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = getToken();
        const savedUser = localStorage.getItem('user_data');
        
        if (token && savedUser) {
          // Verificar se o token ainda é válido
          try {
            const response = await apiService.get('/api/auth/me');
            if (response.data.success) {
              const userData = response.data.user;
              
              console.log('🔍 AuthContext - /api/auth/me retornou:', {
                id: userData.id,
                name: userData.name,
                role: userData.role,
                avatar_url: userData.avatar_url,
                avatar: userData.avatar,
                hasAvatarUrl: !!userData.avatar_url,
                hasAvatar: !!userData.avatar,
                seller_exclusive_owner_id: userData.seller_exclusive_owner_id,
                hasSellerField: 'seller_exclusive_owner_id' in userData
              });
              
              setUser(userData);
              setIsAuthenticated(true);
              // Atualizar dados do usuário no localStorage
              localStorage.setItem('user_data', JSON.stringify(userData));
              localStorage.setItem('user', JSON.stringify(userData));
              console.log('💾 AuthContext - localStorage atualizado com avatar_url:', userData.avatar_url);
              // Disparar evento para atualizar header
              window.dispatchEvent(new CustomEvent('userAvatarUpdated'));
              console.log('📢 AuthContext - Evento userAvatarUpdated disparado');
            } else {
              throw new Error('Token inválido');
            }
          } catch (error) {
            console.error('❌ AuthContext - Token inválido ou expirado:', error);
            clearAuthData();
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('❌ AuthContext - Erro ao verificar autenticação:', error);
        clearAuthData();
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    try {
      
      const response = await apiService.post('/api/auth/login', {
        email,
        password
      });

      if (response.data.success) {
        // ============================================
        // 🔐 VERIFICAR SE REQUER 2FA
        // ============================================
        if (response.data.requires2FA) {
          // Armazenar dados temporários para validação 2FA
          setTwoFactorData({
            userId: response.data.userId,
            email: response.data.email,
            name: response.data.name,
            role: response.data.role,
            expiresIn: response.data.expiresIn || 300
          });
          
          // Abrir modal 2FA
          setShow2FAModal(true);
          
          return { 
            success: true, 
            requires2FA: true,
            message: response.data.message 
          };
        }
        
        // ============================================
        // LOGIN NORMAL (SEM 2FA) - NÃO USADO MAIS
        // ============================================
        const { token, user } = response.data;
        
        // Salvar dados padronizados
        saveToken(token);
        localStorage.setItem('user_data', JSON.stringify(user));
        localStorage.setItem('user', JSON.stringify(user));
        // Disparar evento para atualizar header
        window.dispatchEvent(new CustomEvent('userAvatarUpdated'));

        setUser(user);
        setIsAuthenticated(true);

        return { success: true, user, token };
      } else {
        throw new Error(response.data.message || 'Erro no login');
      }
    } catch (error) {
      console.error('❌ AuthContext - Erro no login:', error);
      console.error('❌ AuthContext - Detalhes do erro:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        url: error.config?.url
      });
      throw error;
    }
  };

  const logout = () => {
    // Limpar dados de autenticação
    clearAuthData();
    
    // Limpar estado
    setUser(null);
    setIsAuthenticated(false);
  };

  const register = async (userData) => {
    try {
      const response = await apiService.post('/api/auth/register', userData);

      if (response.data.success) {
        const { token, user } = response.data;
        
        // Salvar dados padronizados
        saveToken(token);
        localStorage.setItem('user_data', JSON.stringify(user));
        localStorage.setItem('user', JSON.stringify(user));
        // Disparar evento para atualizar header
        window.dispatchEvent(new CustomEvent('userAvatarUpdated'));

        setUser(user);
        setIsAuthenticated(true);

        return { success: true, user };
      } else {
        throw new Error(response.data.message || 'Erro no registro');
      }
    } catch (error) {
      console.error('Erro no registro:', error);
      throw error;
    }
  };

  // ============================================
  // 🔐 FUNÇÕES DE 2FA
  // ============================================
  
  /**
   * Verifica código 2FA e completa o login
   */
  const verify2FACode = async (code) => {
    try {
      if (!twoFactorData) {
        throw new Error('Dados de 2FA não encontrados');
      }

      console.log('🔐 AuthContext - Verificando código 2FA...');

      const response = await apiService.post('/api/auth/complete-2fa', {
        userId: twoFactorData.userId,
        code: code
      });

      if (response.data.success) {
        const { token, user } = response.data;

        console.log('✅ AuthContext - 2FA validado! Completando login...');
        console.log('🔍 AuthContext - User data recebido:', {
          id: user.id,
          role: user.role,
          seller_exclusive_owner_id: user.seller_exclusive_owner_id,
          hasSellerField: 'seller_exclusive_owner_id' in user
        });

        // Salvar dados
        saveToken(token);
        localStorage.setItem('user_data', JSON.stringify(user));
        localStorage.setItem('user', JSON.stringify(user));
        // Disparar evento para atualizar header
        window.dispatchEvent(new CustomEvent('userAvatarUpdated'));

        setUser(user);
        setIsAuthenticated(true);

        // Fechar modal e limpar dados temporários
        setShow2FAModal(false);
        setTwoFactorData(null);

        // Retornar sucesso (navegação será feita pelo modal)
        return { success: true, user, token };
      } else {
        return {
          success: false,
          message: response.data.message || 'Código inválido',
          attemptsLeft: response.data.attemptsLeft
        };
      }
    } catch (error) {
      console.error('❌ AuthContext - Erro ao verificar código 2FA:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erro ao verificar código'
      };
    }
  };

  /**
   * Reenvia código 2FA
   */
  const resend2FACode = async () => {
    try {
      if (!twoFactorData) {
        throw new Error('Dados de 2FA não encontrados');
      }

      console.log('🔐 AuthContext - Reenviando código 2FA...');

      const response = await apiService.post('/api/2fa/resend-code', {
        userId: twoFactorData.userId
      });

      if (response.data.success) {
        console.log('✅ AuthContext - Código 2FA reenviado!');
        
        // Atualizar expiresIn
        setTwoFactorData(prev => ({
          ...prev,
          expiresIn: response.data.expiresIn || 300
        }));

        return { success: true, message: response.data.message };
      } else {
        return {
          success: false,
          message: response.data.message || 'Erro ao reenviar código'
        };
      }
    } catch (error) {
      console.error('❌ AuthContext - Erro ao reenviar código:', error);
      return {
        success: false,
        message: error.response?.data?.message || error.message || 'Erro ao reenviar código'
      };
    }
  };

  /**
   * Fecha modal 2FA e limpa dados temporários
   */
  const close2FAModal = () => {
    setShow2FAModal(false);
    setTwoFactorData(null);
  };

  const updateUser = (userData) => {
    const updatedUser = { ...user, ...userData };
    setUser(updatedUser);
    localStorage.setItem('user_data', JSON.stringify(updatedUser));
    localStorage.setItem('user', JSON.stringify(updatedUser));
    // Disparar evento para atualizar header
    window.dispatchEvent(new CustomEvent('userAvatarUpdated'));
  };

  const getUserId = () => {
    return user?.id || null;
  };

  const getUserRole = () => {
    return user?.role || 'guest';
  };

  const isAdmin = () => {
    return getUserRole() === 'admin';
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    logout,
    register,
    updateUser,
    getUserId,
    getUserRole,
    isAdmin,
    getToken,
    // 🔐 2FA
    show2FAModal,
    twoFactorData,
    verify2FACode,
    resend2FACode,
    close2FAModal
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      
      {/* 🔐 Modal de verificação 2FA */}
      <TwoFactorModal
        show={show2FAModal}
        onHide={close2FAModal}
        onVerify={verify2FACode}
        onResend={resend2FACode}
        email={twoFactorData?.email}
        expiresIn={twoFactorData?.expiresIn}
      />
    </AuthContext.Provider>
  );
};

export default AuthContext; 